// ============================================================
// fondos/paleta.js — EL SEGUNDO COLOR de la decoración de un fondo animado (módulo PURO)
//
// P3 del motor de fondos, fase F3 (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.6, E4). Los fondos con dos colores (aurora, mesh-gradient, blobs-morph) pintan
// el acento y un SEGUNDO color. Ese segundo color era el `--secondary` de la sección tal cual, y
// `--secondary` no es un color de decoración: `contrast.js::secondaryOf` lo contrasta contra el
// fondo para usarlo en badges y filos, y en una sección clara lo oscurece hasta casi negro
// (`#0ea5e9` → `#043348`). De ahí la «banda pizarra» de Aurora y el «lavado gris» de Mesh Flow.
// Sin secundario, cada clase caía a un color fijo (azul marino sobre claro, blanco sobre oscuro)
// que no era de la marca.
//
// Regla (una sola, para el runtime y la vista previa del panel):
//   1. un color2 elegido A MANO en el panel manda, tal cual;
//   2. si no, el secundario de MARCA (`--secondary`, que ya está en el HTML publicado: llega sin
//      republicar) con su MATIZ y su saturación, a la luminosidad de decoración de la polaridad;
//   3. si la marca no tiene secundario, el acento en otro tono (el mismo matiz, más claro sobre
//      claro y más hondo sobre oscuro): un duotono de la marca. Nunca se inventa un matiz
//      (misma disciplina que el ritmo de página, gotcha 24).
//
// El primer color (el acento, con la regla L10 del P2) no se toca: lo decide `efectiva.js`.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, cero imports y cero DOM (lo importan node 12 y el build).
// ============================================================

/** Luminosidad HSL del secundario de marca como color de decoración, por polaridad. */
export const TONO_DECORACION = { claro: 0.6, oscuro: 0.55 };

/** Luminosidad HSL del acompañante del acento cuando la marca no tiene secundario. */
export const TONO_ACOMPANANTE = { claro: 0.74, oscuro: 0.4 };

/** Por debajo de esta saturación HSL un color es gris: se re-tonaliza como gris (sin matiz). */
const SATURACION_GRIS = 0.08;

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** '#rgb' | '#rrggbb' → {r, g, b} (0-255), o null si no es un hex literal. */
export function hexARgb(hex) {
    if (typeof hex !== 'string') return null;
    const m = HEX.exec(hex.trim());
    if (!m) return null;
    const h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbAHex(c) {
    return '#' + [c.r, c.g, c.b].map(function (v) {
        return ('0' + Math.max(0, Math.min(255, Math.round(v))).toString(16)).slice(-2);
    }).join('');
}

/** {r, g, b} 0-255 → {h: 0-360, s: 0-1, l: 0-1}. */
export function rgbAHsl(c) {
    const r = c.r / 255;
    const g = c.g / 255;
    const b = c.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l: l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return { h: h * 60, s: s, l: l };
}

function _canal(p, q, t) {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
}

/** {h: 0-360, s: 0-1, l: 0-1} → {r, g, b} 0-255. */
export function hslARgb(c) {
    const l = c.l;
    const s = c.s;
    if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const h = (((c.h % 360) + 360) % 360) / 360;
    return { r: _canal(p, q, h + 1 / 3) * 255, g: _canal(p, q, h) * 255, b: _canal(p, q, h - 1 / 3) * 255 };
}

/**
 * El mismo color (matiz y saturación) a la luminosidad `l`. Un gris sigue gris.
 * @param {string} hex
 * @param {number} l - luminosidad HSL 0-1
 * @returns {string|null} hex, o null si `hex` no es un color legible
 */
export function aLuminosidad(hex, l) {
    const rgb = hexARgb(hex);
    if (!rgb) return null;
    const hsl = rgbAHsl(rgb);
    const s = hsl.s < SATURACION_GRIS ? 0 : hsl.s;
    return rgbAHex(hslARgb({ h: hsl.h, s: s, l: l }));
}

/**
 * El segundo color de la decoración (ver la cabecera del módulo).
 *
 * @param {{c1?: string, c2Mano?: string, c2Marca?: string, oscuro?: boolean}} datos
 *   `c1` = el acento que pinta el fondo; `c2Mano` = el color2 guardado en la config (el panel);
 *   `c2Marca` = el `--secondary` de la sección; `oscuro` = polaridad de la sección.
 * @returns {string} hex en minúsculas
 */
export function segundoColor(datos) {
    const d = datos || {};
    const pol = d.oscuro ? 'oscuro' : 'claro';
    const mano = hexARgb(d.c2Mano);
    if (mano) return rgbAHex(mano);
    const marca = aLuminosidad(d.c2Marca, TONO_DECORACION[pol]);
    if (marca) return marca;
    const acompanante = aLuminosidad(d.c1, TONO_ACOMPANANTE[pol]);
    if (acompanante) return acompanante;
    // Sin un solo color legible (un acento guardado en otro formato): el índigo de siempre.
    return aLuminosidad('#6366f1', TONO_ACOMPANANTE[pol]);
}
